import { dismissAgentInsight, fetchAgentSummary, generateAgentSummary } from '@api/taptotip';
import { UserRoles } from '@utils/constants';
import { isPaymentsCompany } from '@utils/helpers';
import React, { useEffect, useState } from 'react';

// Card 74 — TipDirect Agent Summaries.
// Renders the latest weekly AI coaching summary as "AI info windows" on the
// admin dashboard. Look and feel deliberately mirrors the PIP's AI Insight
// card: dark slate panel, oversized radius, faint Bot watermark, amber
// Sparkles badge, uppercase kicker, italic body.

// Inline lucide-style icons — the admin app doesn't ship lucide-react, and two
// tiny SVGs aren't worth the dependency.
const SparklesIcon = ({ className, size = 20 }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round">
    <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
    <path d="M20 3v4" />
    <path d="M22 5h-4" />
  </svg>
);

const BotIcon = ({ className, size = 100 }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round">
    <path d="M12 8V4H8" />
    <rect width="16" height="12" x="4" y="8" rx="2" />
    <path d="M2 14h2" />
    <path d="M20 14h2" />
    <path d="M15 13v2" />
    <path d="M9 13v2" />
  </svg>
);

const severityStyles = {
  celebrate: {
    chip: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    label: 'Win'
  },
  suggestion: {
    chip: 'bg-indigo-50 text-indigo-700 border-indigo-100',
    label: 'Suggestion'
  },
  warning: {
    chip: 'bg-amber-50 text-amber-700 border-amber-100',
    label: 'Needs attention'
  }
};

const formatWeekRange = (start, end) => {
  try {
    const options = { month: 'short', day: 'numeric' };
    return `${new Date(start).toLocaleDateString(undefined, options)} – ${new Date(
      end
    ).toLocaleDateString(undefined, options)}`;
  } catch (err) {
    return '';
  }
};

const InsightWindow = ({ insight, onDismiss }) => {
  const styles = severityStyles[insight.severity] || severityStyles.suggestion;

  return (
    <div className="relative bg-white rounded-3xl border border-solid border-slate-200 shadow-sm p-6 min-w-0">
      <button
        type="button"
        aria-label="Dismiss insight"
        title="Dismiss"
        className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center rounded-full border-0 bg-slate-100 hover:bg-slate-200 text-slate-500 cursor-pointer transition-colors text-sm leading-none"
        onClick={() => onDismiss(insight.id)}>
        ✕
      </button>

      <div className="flex items-center gap-2 flex-wrap pr-8">
        <span
          className={`text-[10px] font-bold px-2 py-1 rounded-lg border border-solid uppercase tracking-wider ${styles.chip}`}>
          {styles.label}
        </span>
        {insight.guideName && (
          <span className="text-[10px] font-bold px-2 py-1 rounded-lg border border-solid bg-slate-50 text-slate-600 border-slate-200 uppercase tracking-wider">
            {insight.guideName}
          </span>
        )}
      </div>

      <h4 className="text-sm font-black text-slate-900 mt-3 mb-1">{insight.title}</h4>
      <p className="text-sm text-slate-500 leading-relaxed m-0">{insight.body}</p>

      {insight.action && (
        <div className="flex items-start gap-2 mt-3 text-sm font-semibold text-indigo-600">
          <span aria-hidden="true">→</span>
          <span>{insight.action}</span>
        </div>
      )}
    </div>
  );
};

// A summary older than this is refreshed on dashboard load. Keeps the info
// windows current even before the weekly cron is configured (and gives new
// companies their first summary on first visit instead of an empty dashboard).
const STALE_AFTER_DAYS = 8;

const isStale = summary => {
  if (!summary?.weekEnd) return true;
  const ageMs = Date.now() - new Date(summary.weekEnd).getTime();
  return ageMs > STALE_AFTER_DAYS * 24 * 60 * 60 * 1000;
};

const AgentSummary = ({ user }) => {
  const [summary, setSummary] = useState(null);

  // Payments companies never see (or trigger) AI summaries — matching the
  // backend, which refuses to generate and reads back null for them.
  const isAdmin =
    user?.role === UserRoles.admin && !isPaymentsCompany(user?.companyId, user);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;

    fetchAgentSummary()
      .then(data => {
        if (cancelled) return null;
        if (data && !isStale(data)) {
          setSummary(data);
          return null;
        }
        // Cold start / stale: generate this week's summary now. The backend
        // refuses when the company has the feature switched off, and keeps
        // one document per calendar week, so this is safe to fire-and-forget.
        return generateAgentSummary().then(fresh => {
          if (!cancelled && fresh) setSummary(fresh);
        });
      })
      .catch(err => {
        // Summaries are strict opt-in per company (off by default), so the
        // cold-start generate is expected to be refused for most companies —
        // stay silent for that; the panel simply doesn't render.
        if (err?.details === 'agent_summaries_disabled') return;
        // The dashboard must never break because coaching data is missing.
        console.error('Failed to load agent summary:', err);
      });

    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  if (!isAdmin || !summary || !summary.summaryText) return null;

  const insights = (summary.insights || []).filter(insight => !insight.dismissed);

  const handleDismiss = insightId => {
    // Optimistic removal; dismissal is persisted server-side.
    setSummary(current => ({
      ...current,
      insights: (current.insights || []).filter(insight => insight.id !== insightId)
    }));
    dismissAgentInsight(insightId).catch(err => {
      console.error('Failed to dismiss insight:', err);
    });
  };

  return (
    <div className="flex flex-col gap-4 mb-6" data-testid="agent-summary">
      {/* AI Insight box — same visual contract as the PIP dashboard */}
      <div className="bg-slate-900 rounded-[2.5rem] p-8 text-white relative overflow-hidden shadow-xl">
        <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
          <BotIcon size={100} />
        </div>
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <SparklesIcon size={20} className="text-amber-400" />
            <h4 className="text-xs font-black uppercase tracking-widest text-slate-400 m-0">
              Weekly AI Summary
            </h4>
            <span className="text-xs text-slate-500 font-semibold">
              {formatWeekRange(summary.weekStart, summary.weekEnd)}
            </span>
          </div>
          <p className="text-lg font-medium leading-relaxed italic m-0">
            &ldquo;{summary.summaryText}&rdquo;
          </p>
        </div>
      </div>

      {insights.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {insights.map(insight => (
            <InsightWindow key={insight.id} insight={insight} onDismiss={handleDismiss} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AgentSummary;
